import { ODataObject } from '../../interfaces/odata-model.interface';
import { CustomInjector } from '../../services/custom-injector/custom-injector';
import { ModelOptionsLoaderService } from '../../services/model-options-loader/model-options-loader.service';
import { UserMasterService } from '../../services/user-master/user-master.service';
import { assignModelOptions } from '../../utilities/assign-model-options';
import { JsonTextModel, TextModel } from '../primitive/text.model';
import { CompositeModel } from './composite.model';
import { DropdownModel, JsonDropdownModel } from './dropdown.model';
import { AddressModel } from './address.model';

export class TimesheetAddressModel extends CompositeModel {
    public street: TextModel;
    public city: TextModel;
    public postCode: TextModel;
    public country: TextModel;
    public addrText: TextModel;
    public addrNumber: TextModel;
    public statesOfUSA: DropdownModel<TextModel>;

    public constructor(
        street = '',
        city = '',
        postCode = '',
        country = '',
        addrText = '',
        addrNumber = '',
        stateOfUSA = '',
    ) {
        super('TimesheetAddressModel');

        this.street = <TextModel>this.set(new TextModel('Street', street));
        this.city = <TextModel>this.set(new TextModel('City', city));
        this.postCode = <TextModel>this.set(new TextModel('PostalCode', postCode));
        this.country = <TextModel>this.set(new TextModel('Country', country));
        this.addrText = <TextModel>this.set(new TextModel('AddrNotes', addrText));
        this.addrNumber = <TextModel>this.set(new TextModel('AddrNumber', addrNumber));
        AddressModel.updateStateOfUSA(this, stateOfUSA);

        const injector = CustomInjector.getInjector();
        const modelOptionsLoader = injector.get(ModelOptionsLoaderService);
        const userMasterService = injector.get(UserMasterService);

        modelOptionsLoader.load(this.street, 'Timesheet', 'Street');
        modelOptionsLoader.load(this.city, 'Timesheet', 'City');
        modelOptionsLoader.load(this.postCode, 'Timesheet', 'PostCode');
        modelOptionsLoader.load(this.country, 'Timesheet', 'Country');
        modelOptionsLoader.load(this.addrText, 'Timesheet', 'AddrText');
        modelOptionsLoader.load(this.statesOfUSA, 'Timesheet', 'State');

        // For release 1 country field is mandatory for new addresses and populated from user data table pernr (which has value 'DE' for all users) and is non-editable.
        this.country.value = userMasterService.getSelectedUserModel().getCountry();
        this.country = assignModelOptions(this.country, {
            ...this.country.options,
            readonly: true,
        });
    }

    public isValid(): boolean {
        return this.street.value.length > 0 && this.city.value.length > 0 && this.postCode.value.length > 0;
    }

    public static compare(firstAddress: TimesheetAddressModel, secondAddress: TimesheetAddressModel): boolean {
        return (
            firstAddress.street.value === secondAddress.street.value &&
            firstAddress.city.value === secondAddress.city.value &&
            firstAddress.postCode.value === secondAddress.postCode.value &&
            firstAddress.country.value === secondAddress.country.value &&
            firstAddress.addrText.value === secondAddress.addrText.value &&
            firstAddress.statesOfUSA?.selectedEntry?.model.value ===
                secondAddress.statesOfUSA?.selectedEntry?.model.value
        );
    }

    public equalsTo(other: TimesheetAddressModel): boolean {
        return TimesheetAddressModel.compare(this, other);
    }

    public getOutModel(): ODataTimesheetAddressOutModel {
        const outModel = <ODataTimesheetAddressOutModel>super.getOutModel();
        // Selected StateOfUSA should be sent to backend in city field in <city>;<state> format
        // Example: "Los Angels;CA"
        if (this.statesOfUSA && this.statesOfUSA.selectedEntry?.model.value) {
            outModel[this.city.name] = `${this.city.value};${this.statesOfUSA.selectedEntry.model.value}`;
        }
        return <ODataTimesheetAddressOutModel>super.getOutModel();
    }
}

export class TimesheetAddressModelFromJson extends TimesheetAddressModel {
    public constructor(data: JsonTimesheetAddressModel) {
        super(
            data.street.value,
            data.city.value,
            data.postCode.value,
            data.country.value,
            data.addrText.value,
            data.addrNumber.value,
            <string>data.statesOfUSA?.selectedEntries[0]?.model.value,
        );
    }
}

export interface JsonTimesheetAddressModel {
    street: JsonTextModel;
    city: JsonTextModel;
    postCode: JsonTextModel;
    country: JsonTextModel;
    addrText: JsonTextModel;
    addrNumber: JsonTextModel;
    statesOfUSA: JsonDropdownModel;
}

export interface ODataTimesheetAddressOutModel extends ODataObject {
    Street: string;
    City: string;
    PostalCode: string;
    Country: string;
    AddrNotes: string;
    AddrNumber: string;
}
